// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";

import { ChargerCommand } from "graphql/api";
import { isSet } from "shared/utils/utils";
import { ActionType, IAction, IActionChargerCommand, raiseAction } from "actions";
import { IState } from "states/states";
import { IRfid } from "states/ocppstates";
import { ModalPanel } from "states/guistates";
import Rfid from "model/rfid";

interface IModalStartTransactionExtProps {
  chargerId: number;
}

interface IModalStartTransactionProps {
  isOpen: boolean;
  connector: number | undefined;
  rfids: IRfid[];
}

const mapStateToProps = (state: IState) => {
  return {
    isOpen: state.gui.modal.panel === ModalPanel.START_TRANSACTION,
    connector: state.router.connector,
    rfids: state.ocpp.rfid,
  };
};

interface IModalStartTransactionState {
  selectedRfid: number | undefined;
}

interface IModalStartTransactionDispatch {
  onOkClick: (rfid: string, connector: number | undefined) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IModalStartTransactionExtProps,
) => {
  const dispatchProps: IModalStartTransactionDispatch = {
    onOkClick: (rfid: string, connector: number | undefined) => {
      const payload: IActionChargerCommand = {
        chargerId: ownProps.chargerId,
        cmd: ChargerCommand.StartTransaction,
        cmdArgs: {
          connectorId: connector,
          idTag: rfid,
        },
        timestampMs: Date.now(),
      };
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
      dispatch(raiseAction(ActionType.ACT_TRIGGER_CHARGER_CMD_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  rfidSelectionCaption: {
    id: "Select user",
  },
  noRfidFound: {
    id: "No charging card found.",
  },
});

type IRfidOption = {
  // index into the rfid list
  value: number;
  label: string;
};

class ModalStartTransactionComponent extends React.Component<
  IModalStartTransactionProps & IModalStartTransactionDispatch,
  IModalStartTransactionState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public state: IModalStartTransactionState = {
    selectedRfid: undefined,
  };

  public render() {
    const rfid =
      isSet(this.state.selectedRfid) ? this.props.rfids[this.state.selectedRfid] : undefined;

    return (
      <Modal
        show={this.props.isOpen}
        onHide={this.props.onCancelClick}
        id="modal-start-transaction"
      >
        <Modal.Header closeButton>
          <FormattedMessage id="Starting charging session" />
        </Modal.Header>
        <Modal.Body>
          <p>
            <FormattedMessage id="Which user shall be associated to this charging session?" />
          </p>
          {this.renderRfidSelection()}
          <p className="mt-4">
            <FormattedMessage id="This will start a new charging session and authorize the selected user." />
          </p>
          <p className="mb-4">
            <FormattedMessage id="Please make sure that the vehicle is ready and already plugged to the charger." />
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            id="confirmCancel"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            id="confirmOk"
            variant="primary"
            disabled={!rfid}
            onClick={(ev) => this.props.onOkClick(rfid!.rfid, this.props.connector)}
          >
            <FormattedMessage id="Authorize Charging" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderRfidSelection() {
    const activeIndexes = Array.from(Array(this.props.rfids.length).keys()).filter(
      (curIndex) => this.props.rfids[curIndex].isActive,
    );
    // several cards may share a name; add the card number to distinguish them
    const isNameShared = (name: string) =>
      activeIndexes.filter((curIndex) => this.props.rfids[curIndex].name === name).length > 1;
    const idToLabel = (index: number) => {
      const rfid = this.props.rfids[index];
      if (!rfid?.name) {
        return rfid?.rfid ?? "";
      }
      return isNameShared(rfid.name) ? `${rfid.name} (${rfid.rfid})` : rfid.name;
    };
    const options: IRfidOption[] = activeIndexes
      .map((curIndex) => ({ value: curIndex, label: idToLabel(curIndex) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return (
      // searchable selection, as there may be many users
      <Select<IRfidOption>
        options={options}
        value={options.find((curOption) => curOption.value === this.state.selectedRfid) ?? null}
        onChange={(option) => this.setState({ selectedRfid: option?.value })}
        // match card number and category as well, not just the shown name
        filterOption={(option, inputValue) =>
          new Rfid(this.props.rfids[option.data.value]).matchesFilter(inputValue)
        }
        placeholder={`<${this.context.formatMessage(messages.rfidSelectionCaption)}>`}
        noOptionsMessage={() => this.context.formatMessage(messages.noRfidFound)}
        maxMenuHeight={300}
        // render menu outside the modal so that it may extend beyond the dialog
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
        autoFocus
      />
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ModalStartTransactionComponent);
